import { Fragment, ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

import seo from '../../data/seo.json';
import {
  getStructuredData,
  SeoPageKey,
} from '../../seo/structuredData';

type Props = {
  children: ReactNode;
  seoKey: SeoPageKey;
};

const PageLayout = ({ children, seoKey }: Props): JSX.Element => {
  const page = seo.pages[seoKey];
  const canonicalUrl = `${seo.site.url}${page.path === '/' ? '' : page.path}`;
  const imageUrl = `${seo.site.url}${seo.site.image}`;
  const robots = page.index
    ? 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
    : 'noindex,nofollow,noarchive';

  return (
    <Fragment>
      <Helmet htmlAttributes={{ lang: seo.site.language }}>
        <title>{page.title}</title>
        <meta name="description" content={page.description} />
        <meta name="robots" content={robots} />
        <meta name="author" content={seo.site.name} />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={seo.site.name} />
        <meta property="og:locale" content={seo.site.locale} />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={seo.site.imageAlt} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={page.title} />
        <meta name="twitter:description" content={page.description} />
        <meta name="twitter:image" content={imageUrl} />
        <meta name="twitter:image:alt" content={seo.site.imageAlt} />

        <script type="application/ld+json">
          {JSON.stringify(getStructuredData(seoKey)).replace(/</g, '\\u003c')}
        </script>
      </Helmet>

      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </Fragment>
  );
};

export default PageLayout;
